import { useMemo } from 'react';
import { useCurrentUser } from '@qualio/ui-components';
import { TEMPLATE_STATUS, TemplateDetail } from '../types';

export type TemplateActions = {
  isInherited: boolean;
  canEdit: boolean;
  canAddSections: boolean;
  canDelete: boolean;
  canChangeOwner: boolean;
  canMakeEffective: boolean;
  canCreateDraft: boolean;
  canArchive: boolean;
  canRestore: boolean;
  canEditProperties: boolean;
  canExport: boolean;
  canUpgradeDocuments: boolean;
  canViewChangeControl: boolean;
  canManageAccountAccess: boolean;
};

const NO_ACTIONS: Omit<TemplateActions, 'isInherited'> = {
  canEdit: false,
  canAddSections: false,
  canDelete: false,
  canChangeOwner: false,
  canMakeEffective: false,
  canCreateDraft: false,
  canArchive: false,
  canRestore: false,
  canEditProperties: false,
  canExport: false,
  canUpgradeDocuments: false,
  canViewChangeControl: false,
  canManageAccountAccess: false,
};

export const isInheritedTemplate = (template: Pick<TemplateDetail, 'parent'>): boolean => !!template.parent;

export const useTemplateActions = (template: TemplateDetail): TemplateActions => {
  const { userId } = useCurrentUser();

  return useMemo(() => {
    const isInherited = isInheritedTemplate(template);
    const isOwner = template.owner?.user_id === userId;
    const isDraft = template.status === TEMPLATE_STATUS.draft;
    const isEffective = template.status === TEMPLATE_STATUS.effective;

    if (isInherited) {
      return { isInherited, ...NO_ACTIONS, canUpgradeDocuments: isEffective };
    }

    const isLatestVersion = template.id === template.latest_template_id;

    return {
      isInherited,
      canEdit: isDraft && isOwner,
      canAddSections: isOwner,
      canDelete: isDraft && isOwner,
      canChangeOwner: isDraft,
      canMakeEffective: isDraft && isOwner,
      canCreateDraft: isEffective && isLatestVersion,
      canArchive: isEffective,
      canRestore: template.status === TEMPLATE_STATUS.archived,
      canEditProperties: isDraft && isOwner,
      canExport: true,
      canUpgradeDocuments: isEffective,
      canViewChangeControl: true,
      canManageAccountAccess: isEffective || isDraft,
    };
  }, [template, userId]);
};
